class ScheduleCacheService {
  constructor() {
    this.CACHE_PREFIX = 'schedule_cache_';
    this.TTL_MINUTES = 15;
  }

  /**
   * Generate cache key based on area, year and month
   */
  generateCacheKey(area, year, month) {
    return `${this.CACHE_PREFIX}${area}_${year}_${month}`;
  }

  /**
   * Check if cache is valid (not expired)
   */
  isValid(cacheData) {
    if (!cacheData || !cacheData.timestamp) {
      return false;
    }

    const now = new Date().getTime();
    const cacheTime = new Date(cacheData.timestamp).getTime();
    const ttlMs = this.TTL_MINUTES * 60 * 1000;

    return (now - cacheTime) < ttlMs;
  }

  /**
   * Save data to localStorage with timestamp
   */
  set(area, year, month, data) {
    try {
      const key = this.generateCacheKey(area, year, month);
      const cacheData = {
        data,
        timestamp: new Date().toISOString(),
        area,
        year,
        month
      };

      localStorage.setItem(key, JSON.stringify(cacheData));
      return true;
    } catch (error) {
      console.error('[CACHE] Error saving to localStorage:', error);
      return false;
    }
  }

  /**
   * Get data from localStorage if still valid
   */
  get(area, year, month) {
    try {
      const key = this.generateCacheKey(area, year, month);
      const cached = localStorage.getItem(key);

      if (!cached) {
        return null;
      }

      const cacheData = JSON.parse(cached);

      if (!this.isValid(cacheData)) {
        this.clear(area, year, month);
        return null;
      }

      return cacheData.data;
    } catch (error) {
      console.error('[CACHE] Error reading from localStorage:', error);
      return null;
    }
  }

  /**
   * Clear specific cache
   */
  clear(area, year, month) {
    try {
      const key = this.generateCacheKey(area, year, month);
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error('[CACHE] Error clearing cache:', error);
      return false;
    }
  }

  /**
   * Clear all schedule caches
   */
  clearAll() {
    try {
      const keys = Object.keys(localStorage);
      const scheduleKeys = keys.filter(key => key.startsWith(this.CACHE_PREFIX));

      scheduleKeys.forEach(key => {
        localStorage.removeItem(key);
      });

      return true;
    } catch (error) {
      console.error('[CACHE] Error clearing all caches:', error);
      return false;
    }
  }

  /**
   * List all stored caches
   */
  listCaches() {
    try {
      const keys = Object.keys(localStorage);
      const scheduleKeys = keys.filter(key => key.startsWith(this.CACHE_PREFIX));

      return scheduleKeys.map(key => {
        const cached = localStorage.getItem(key);
        if (cached) {
          const cacheData = JSON.parse(cached);
          return {
            key,
            area: cacheData.area,
            year: cacheData.year,
            month: cacheData.month,
            timestamp: cacheData.timestamp,
            isValid: this.isValid(cacheData)
          };
        }
        return null;
      }).filter(Boolean);
    } catch (error) {
      console.error('[CACHE] Error listing caches:', error);
      return [];
    }
  }

  /**
   * Set custom TTL
   */
  setTTL(minutes) {
    this.TTL_MINUTES = minutes;
  }
}

export default new ScheduleCacheService();
